import React, { useState } from "react";
import { api } from "../lib/api";
import { blogErrorMessage } from "../lib/blogErrors";
import { useT } from "../lib/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MarkdownBody from "../components/MarkdownBody";
import { toast } from "sonner";
import { FileText, X } from "lucide-react";
import MediaPickerField from "./site/MediaPickerField";
import {
  BLOG_LANGS, completeLanguages, slugFromTitle, startedLanguages, toBlogPayload, validateBlogForm,
} from "./blogPayload";

const BLOG_API = "/super-admin/hq/blog";
const FIELD = "w-full bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-800";
const LABEL = "block text-xs text-slate-500 mb-1";

const EMPTY_FORM = { slug: "", cover_media_id: null, cover_url: "", published: false };

/**
 * Create / edit overlay. FR, EN and ES share one form: each tab edits that language's
 * Title, Excerpt, Tag and Body, and the slug, cover and publication switch are shared.
 * Any ONE complete language (title and body) is enough to save.
 *
 * @param {{initial: object, onClose: () => void, onDone: () => void}} props `initial` is
 *   `{}` for a new post, or the FULL post being edited (it carries `post_id`).
 */
export default function HqBlogEditor({ initial, onClose, onDone }) {
  const t = useT("superadmin");
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initial });
  // Opens on the first language that has content, so an English-only post is not shown as
  // an empty French tab.
  const [lang, setLang] = useState(() => completeLanguages(form)[0] || BLOG_LANGS[0]);
  const [bodyMode, setBodyMode] = useState("write");
  const [error, setError] = useState("");
  const complete = completeLanguages(form);
  const started = startedLanguages(form);
  // empty (nothing typed) / incomplete (started, needs a title and a body) / complete.
  const stateOf = (l) => (complete.includes(l) ? "complete" : started.includes(l) ? "incomplete" : "empty");
  const DOT = { complete: "bg-emerald-500", incomplete: "bg-amber-500", empty: "bg-slate-300" };
  // The slug follows the first title until the operator types one; an existing post's slug
  // is a published URL, so it is never re-suggested.
  const [slugTouched, setSlugTouched] = useState(Boolean(initial.post_id));

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const setLangField = (name, value) => {
    setForm((f) => {
      const next = { ...f, [`${name}_${lang}`]: value };
      if (name === "title" && !slugTouched) {
        const first = BLOG_LANGS.map((l) => next[`title_${l}`]).find((v) => slugFromTitle(v));
        next.slug = slugFromTitle(first);
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    const invalid = validateBlogForm(form);
    if (invalid) {
      setError(invalid.code === "BLOG_LANGUAGE_INCOMPLETE"
        ? t("hqblog.err.languageIncomplete", { lang: t(`hqblog.lang.${invalid.lang}`) })
        : t("hqblog.err.noLanguage"));
      return;
    }
    if (!form.slug) { setError(t("hqblog.err.slugRequired")); return; }
    setError("");
    try {
      if (initial.post_id) await api.put(`${BLOG_API}/${initial.post_id}`, toBlogPayload(form));
      else await api.post(BLOG_API, toBlogPayload(form));
      toast.success(t("hqblog.saved"));
      onDone();
    } catch (err) {
      toast.error(blogErrorMessage(err, t));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-stretch justify-end" onClick={onClose}>
      <form noValidate onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-white border-l border-slate-200 overflow-y-auto" data-testid="blog-editor">
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-100 p-6 flex items-center justify-between z-10">
          <div className="font-sans text-2xl text-slate-800">{initial.post_id ? t("hqblog.editTitle") : t("hqblog.newTitle")}</div>
          <button type="button" onClick={onClose} aria-label={t("hqblog.close")}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div role="tablist" className="flex gap-2 border-b border-slate-200">
            {BLOG_LANGS.map((l) => (
              <button
                key={l}
                type="button"
                role="tab"
                aria-selected={l === lang}
                onClick={() => setLang(l)}
                data-state={stateOf(l)}
                className={`px-4 py-2 text-sm -mb-px border-b-2 inline-flex items-center gap-2 ${l === lang ? "border-sol-gold text-slate-900" : "border-transparent text-slate-500"}`}
                data-testid={`blog-tab-${l}`}
              >
                <span className={`w-2 h-2 rounded-full ${DOT[stateOf(l)]}`} aria-hidden="true" />
                {t(`hqblog.lang.${l}`)}
              </button>
            ))}
          </div>

          <div>
            <label className={LABEL} htmlFor="blog-title">{t("hqblog.fTitle")}</label>
            <Input id="blog-title" value={form[`title_${lang}`] || ""} onChange={(e) => setLangField("title", e.target.value)} className="bg-white border-slate-200" data-testid="blog-title" />
          </div>
          <div>
            <label className={LABEL} htmlFor="blog-excerpt">{t("hqblog.fExcerpt")}</label>
            <textarea id="blog-excerpt" rows={2} value={form[`excerpt_${lang}`] || ""} onChange={(e) => setLangField("excerpt", e.target.value)} className={FIELD} data-testid="blog-excerpt" />
            <p className="text-xs text-slate-400 mt-1">{t("hqblog.excerptHint")}</p>
          </div>
          <div>
            <label className={LABEL} htmlFor="blog-tag">{t("hqblog.fTag")}</label>
            <Input id="blog-tag" value={form[`tag_${lang}`] || ""} onChange={(e) => setLangField("tag", e.target.value)} className="bg-white border-slate-200" data-testid="blog-tag" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs text-slate-500" htmlFor="blog-body">{t("hqblog.fBody")}</label>
              <div className="flex gap-1 text-xs">
                {["write", "preview"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setBodyMode(mode)}
                    className={`px-2 py-1 rounded ${bodyMode === mode ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600"}`}
                    data-testid={`blog-body-${mode}`}
                  >
                    {t(mode === "write" ? "hqblog.write" : "hqblog.preview")}
                  </button>
                ))}
              </div>
            </div>
            {bodyMode === "write" ? (
              <>
                <textarea id="blog-body" rows={14} value={form[`body_${lang}`] || ""} onChange={(e) => setLangField("body", e.target.value)} className={`${FIELD} font-mono`} data-testid="blog-body" />
                <p className="text-xs text-slate-400 mt-1">{t("hqblog.bodyHint")}</p>
              </>
            ) : (
              <div className="min-h-[16rem] rounded-md border border-slate-200 p-4" data-testid="blog-preview-pane">
                <MarkdownBody source={form[`body_${lang}`] || ""} tone="light" />
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 pt-4 space-y-4">
            <div>
              <label className={LABEL} htmlFor="blog-slug">{t("hqblog.fSlug")}</label>
              <Input id="blog-slug" value={form.slug} onChange={(e) => { setSlugTouched(true); setField("slug", e.target.value); }} className="bg-white border-slate-200 font-mono text-xs" data-testid="blog-slug" />
              <p className="text-xs text-slate-400 mt-1">{t("hqblog.slugHint")}</p>
            </div>
            <MediaPickerField
              label={t("hqblog.cover")}
              // A legacy cover (served, but no library id) is shown so it can be replaced or
              // removed; `toBlogPayload` leaves it alone until then.
              value={form.cover_media_id || form.cover_url
                ? { media_id: form.cover_media_id, url: form.cover_url } : null}
              onChange={(picked) => setForm((f) => ({ ...f, cover_media_id: picked.media_id, cover_url: picked.url }))}
              onClear={() => setForm((f) => ({ ...f, cover_media_id: null, cover_url: "" }))}
              testId="blog-cover"
            />
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={(e) => setField("published", e.target.checked)} data-testid="blog-publish" />
              <span className="text-sm text-slate-700">{t("hqblog.fPublished")}</span>
            </label>
          </div>

          {error && <p role="alert" className="text-sm text-rose-600" data-testid="blog-form-error">{error}</p>}
          <Button type="submit" className="w-full bg-sol-gold text-black hover:bg-amber-200" data-testid="blog-save">
            <FileText className="w-4 h-4 mr-2" /> {t("hqblog.savecta")}
          </Button>
        </div>
      </form>
    </div>
  );
}
